"use client"

import type { MobileReleaseChannelReport, MobileReleaseControl, OtaBundleInfo } from "@repo/types"
import { AlertTriangle, RotateCcw, Smartphone } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { DendromapLoader } from "../../components/dendromap-loader"
import { Alert, AlertDescription } from "../../components/ui/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { StatusBadge } from "../../components/ui/data-table/status-badge"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table"
import { Textarea } from "../../components/ui/textarea"
import { useMobileReleases, useUpdateMobileRelease } from "../../hooks/api/platform/use-platform"
import { useLabels } from "../../hooks/use-labels"
import { PageHeader } from "../../ui/layout/page-header"
import { apiErrorMessage } from "../superadmin/client-detail.helpers"

const CHANNEL_LABELS = { prod: "Production", preprod: "Préproduction" } as const

const formatSize = (bytes: number) => `${(bytes / (1024 * 1024)).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} Mo`
const formatDateTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"

/** Espace /superadmin : versions de l'app mobile distribuées par mise à jour à distance (OTA). */
export function MobileReleasesPage() {
  const { labels } = useLabels()
  const { data: channels, isLoading, error } = useMobileReleases()

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeader.Title>{labels.sidebar.superadmin.mobileReleases}</PageHeader.Title>
        <PageHeader.Description>
          Bundles publiés par la CI (workflow « Publish OTA »), version distribuée aux téléphones, retour arrière et version
          native minimale. Les téléphones vérifient au démarrage et au retour dans l&apos;application.
        </PageHeader.Description>
      </PageHeader>
      {isLoading ? (
        <DendromapLoader message="Chargement des versions..." />
      ) : error || !channels ? (
        <Alert variant="destructive"><AlertDescription>{apiErrorMessage(error, "Versions indisponibles.")}</AlertDescription></Alert>
      ) : (
        channels.map((channel) => <ChannelCard key={channel.channel} report={channel} />)
      )}
    </div>
  )
}

function ChannelCard({ report }: { report: MobileReleaseChannelReport }) {
  const updateRelease = useUpdateMobileRelease()
  const [rollbackTarget, setRollbackTarget] = useState<OtaBundleInfo | null>(null)
  const { control, published } = report
  const pinned = control.pinned_version && control.pinned_version !== published?.version ? control.pinned_version : null

  const save = async (next: MobileReleaseControl, success: string) => {
    try {
      await updateRelease.mutateAsync({ channel: report.channel, control: next })
      toast.success(success)
    } catch (error) {
      toast.error(apiErrorMessage(error, "Enregistrement impossible"))
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <Smartphone className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-base">{CHANNEL_LABELS[report.channel]}</CardTitle>
          <StatusBadge tone={pinned ? "warning" : "active"} label={`Distribuée : ${report.effectiveVersion ?? "—"}`} />
        </div>
        <CardDescription>
          {published
            ? `Dernière publication : ${published.version} le ${formatDateTime(published.publishedAt)} · natif minimum ${published.minBuildNumber}${published.mandatory ? " · obligatoire" : ""}`
            : `Aucun manifest publié${report.publishedError ? ` (${report.publishedError})` : ""}.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {pinned && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
              <span>
                Retour arrière actif : les téléphones installent <strong>{pinned}</strong> au lieu de {published?.version}.
              </span>
              <Button size="sm" variant="outline" disabled={updateRelease.isPending} onClick={() => save({ ...control, pinned_version: null }, "Dernière version rétablie")}>
                Revenir à la dernière publication
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {published?.releaseNotes && !pinned && <p className="text-sm text-muted-foreground whitespace-pre-line">{published.releaseNotes}</p>}

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">Bundles sur le serveur</h3>
          {report.bundles.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun bundle publié sur ce canal.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Version</TableHead>
                    <TableHead>Envoyé le</TableHead>
                    <TableHead className="text-right">Taille</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.bundles.map((bundle) => {
                    const isEffective = bundle.version === report.effectiveVersion
                    const isLatest = bundle.version === published?.version
                    return (
                      <TableRow key={bundle.filename}>
                        <TableCell className="font-medium">
                          {bundle.version}
                          {isLatest && <span className="ml-2 text-xs text-muted-foreground">dernière publication</span>}
                        </TableCell>
                        <TableCell>{formatDateTime(bundle.uploadedAt)}</TableCell>
                        <TableCell className="text-right">{formatSize(bundle.size)}</TableCell>
                        <TableCell className="text-right">
                          {isEffective ? (
                            <StatusBadge tone="active" label="Distribuée" />
                          ) : isLatest ? (
                            <Button size="sm" variant="outline" disabled={updateRelease.isPending} onClick={() => save({ ...control, pinned_version: null }, "Dernière version rétablie")}>
                              Rétablir
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" disabled={updateRelease.isPending} onClick={() => setRollbackTarget(bundle)}>
                              <RotateCcw className="mr-1 h-4 w-4" /> Revenir à cette version
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            La CI ne garde que les 5 derniers bundles. Un retour arrière s&apos;applique aux téléphones dont l&apos;app sait le
            recevoir (bundles publiés après cette fonctionnalité) ; les autres restent sur leur version.
          </p>
        </section>

        <NativeMinimumForm report={report} pending={updateRelease.isPending} onSave={save} />
      </CardContent>

      <AlertDialog open={rollbackTarget !== null} onOpenChange={(open) => !open && setRollbackTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revenir à la version {rollbackTarget?.version} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les téléphones du canal « {CHANNEL_LABELS[report.channel]} » installeront cette version à leur prochain démarrage,
              sans pouvoir refuser. Les publications suivantes de la CI ne seront pas distribuées tant que le retour arrière est actif.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => rollbackTarget && save({ ...control, pinned_version: rollbackTarget.version }, `Retour à ${rollbackTarget.version} activé`)}
            >
              Revenir à cette version
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}

function NativeMinimumForm({
  report,
  pending,
  onSave,
}: {
  report: MobileReleaseChannelReport
  pending: boolean
  onSave: (control: MobileReleaseControl, success: string) => void
}) {
  const id = (name: string) => `${report.channel}-${name}`
  const [draft, setDraft] = useState(report.control)
  useEffect(() => setDraft(report.control), [report.control])

  return (
    <section className="space-y-3 border-t border-border pt-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Version native minimale</h3>
        <p className="text-xs text-muted-foreground">
          versionCode Android / build iOS. En dessous, l&apos;app affiche un écran bloquant qui renvoie vers le store. À utiliser
          quand une version du store corrige un problème natif. Vide = aucun minimum.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor={id("min-native")}>Build natif minimum</Label>
          <Input
            id={id("min-native")}
            type="number"
            min={0}
            className="w-36"
            value={draft.min_native_build ?? ""}
            onChange={(e) => setDraft({ ...draft, min_native_build: e.target.value === "" ? null : Number(e.target.value) })}
          />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor={id("native-message")}>Message affiché</Label>
          <Textarea
            id={id("native-message")}
            value={draft.native_update_message}
            maxLength={300}
            placeholder="Cette version de l'application n'est plus prise en charge. Installez la dernière version depuis le store."
            onChange={(e) => setDraft({ ...draft, native_update_message: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={id("android-url")}>Lien Google Play</Label>
          <Input id={id("android-url")} type="url" value={draft.android_store_url ?? ""} onChange={(e) => setDraft({ ...draft, android_store_url: e.target.value || null })} />
        </div>
        <div className="space-y-1">
          <Label htmlFor={id("ios-url")}>Lien App Store</Label>
          <Input id={id("ios-url")} type="url" value={draft.ios_store_url ?? ""} onChange={(e) => setDraft({ ...draft, ios_store_url: e.target.value || null })} />
        </div>
      </div>
      <div className="flex justify-end">
        <Button disabled={pending} onClick={() => onSave({ ...draft, pinned_version: report.control.pinned_version }, "Version native minimale enregistrée")}>
          Enregistrer
        </Button>
      </div>
    </section>
  )
}
